import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import ShadeMap from 'mapbox-gl-shadow-simulator'
import SunCalc from 'suncalc'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { SHADEMAP_API_KEY } from '../../../../../config/constants'
import { useSunDirectionLayer } from './useSunDirectionLayer'

interface UseSunlightLayersProps {
  map: React.RefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.RefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

// Global reference to ShadeMap instance
let shadeMapInstance: ShadeMap | null = null

// Global reference to mousemove handler for cleanup
let mouseMoveHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null

/**
 * Hook to handle sunlight study layers (shadow simulation and sunlight hours)
 * Uses mapbox-gl-shadow-simulator library with Swiss terrain data
 */
export const useSunlightLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseSunlightLayersProps) => {
  // Handle sun direction indicator layer
  useSunDirectionLayer({
    map,
    mapLoaded,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle shadow simulation and sunlight hours layers - initialization and mode changes
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    const activeSection = (otherMapOptions as any).activeSection
    const isSunlightSection =
      activeSection === 'sunlight' || activeSection === 'sunlight_study'
    const layerStates = (otherMapOptions as any).demographicsLayerStates
    const sunlightConfig = (otherMapOptions as any).sectionData?.sunlightConfig

    const shadowLayerId = 'sunlight-shadow-simulation'
    const sunlightHoursLayerId = 'sunlight-hours'

    // Get layer states
    const shadowLayerConfig = getLayerConfig(shadowLayerId)
    const sunlightHoursLayerConfig = getLayerConfig(sunlightHoursLayerId)

    const shadowLayerState = layerStates?.[shadowLayerId] || {
      enabled: shadowLayerConfig?.defaultEnabled !== false,
      opacity: shadowLayerConfig?.defaultOpacity || 70,
    }

    const sunlightHoursLayerState = layerStates?.[sunlightHoursLayerId] || {
      enabled: sunlightHoursLayerConfig?.defaultEnabled !== false,
      opacity: sunlightHoursLayerConfig?.defaultOpacity || 70,
    }

    // Determine which mode is active (exclusive group - only one can be enabled)
    const isShadowMode = shadowLayerState.enabled
    const isSunlightHoursMode = !isShadowMode && sunlightHoursLayerState.enabled

    // Determine if we should show ShadeMap
    const shouldShowShadeMap =
      isSunlightSection && (isShadowMode || isSunlightHoursMode)

    // Helper function to set up sunlight hours tooltip
    const setupSunlightHoursTooltip = () => {
      if (!map.current || !shadeMapInstance) return

      // Remove existing handler if any
      if (mouseMoveHandler) {
        map.current.off('mousemove', mouseMoveHandler)
      }

      // Create new handler
      mouseMoveHandler = (e: mapboxgl.MapMouseEvent) => {
        if (!shadeMapInstance) return

        const { point } = e
        const hours = shadeMapInstance.getHoursOfSun(point.x, point.y)

        if (hours !== null && hours !== undefined) {
          const hoursRounded = Math.round(hours * 10) / 10 // Round to 1 decimal

          if (!popup.current) {
            popup.current = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              className: 'sunlight-hours-popup',
            })
          }

          popup.current
            .setLngLat(e.lngLat)
            .setHTML(
              `<div style="padding: 4px 8px;"><strong>${hoursRounded} hours</strong> of sun</div>`
            )
            .addTo(map.current!)
        }
      }

      // Add handler to map
      map.current.on('mousemove', mouseMoveHandler)
    }

    // Helper function to remove sunlight hours tooltip
    const removeSunlightHoursTooltip = () => {
      if (mouseMoveHandler && map.current) {
        map.current.off('mousemove', mouseMoveHandler)
        mouseMoveHandler = null
      }
      if (popup.current) {
        popup.current.remove()
      }
    }

    // Only create/update ShadeMap if we should show it
    if (shouldShowShadeMap) {
      // Get map center coordinates for sunrise/sunset calculation
      const center = map.current.getCenter()
      const currentDate = sunlightConfig?.date || new Date()

      // Calculate sunrise and sunset times for the current date and location
      const sunTimes = SunCalc.getTimes(currentDate, center.lat, center.lng)

      // Create ShadeMap instance if it doesn't exist
      if (!shadeMapInstance) {
        try {
          // Get API key from environment
          const apiKey = SHADEMAP_API_KEY as string

          if (!apiKey) {
            console.error(
              '[ShadeMap] VITE_SHADEMAP_API_KEY environment variable is not set. Shadow visualization will not work.'
            )
            return
          }

          // Swiss terrain configuration
          // Use proxy in development to bypass CORS, direct URL in production
          const shadeMapBaseUrl =
            import.meta.env.DEV && window.location.hostname === 'localhost'
              ? '/shademap-tiles'
              : 'https://cfw.shademap.app'

          const terrainSource = {
            maxZoom: 16,
            tileSize: 512,
            getSourceUrl: ({ x, y, z }: { x: number; y: number; z: number }) =>
              `${shadeMapBaseUrl}/switzerland/${z}/${x}/${y}.webp`,
            getElevation: ({ r, g }: { r: number; g: number }) =>
              ((r * 256 + g) / 2 ** 16) * 4808,
            _overzoom: 18,
          }

          // Determine initial opacity based on which mode is active
          const initialOpacity = isShadowMode
            ? shadowLayerState.opacity / 100
            : sunlightHoursLayerState.opacity / 100

          // Create ShadeMap instance with all required options
          shadeMapInstance = new ShadeMap({
            map: map.current,
            apiKey,
            date: currentDate,
            terrainSource,
            color: '#000000',
            opacity: initialOpacity,
            // Debug function for development mode
            ...(import.meta.env.DEV && {
              debug: (...args: any[]) =>
                console.log('[ShadeMap Debug]', ...args),
            }),
          })

          // Add to map
          shadeMapInstance.addTo(map.current)

          // Enable sun exposure mode if in sunlight hours mode
          if (isSunlightHoursMode) {
            shadeMapInstance.setSunExposure(true, {
              startDate: sunTimes.sunrise,
              endDate: sunTimes.sunset,
              iterations: 32, // Number of samples for calculation
            } as any)

            // Set up tooltip to show hours of sun on hover
            // Wait a bit for the sun exposure calculation to complete
            setTimeout(() => {
              setupSunlightHoursTooltip()
            }, 100)
          }
        } catch (error) {
          console.error('Error creating ShadeMap instance:', error)
          return
        }
      } else {
        // Update existing ShadeMap instance - handle mode changes
        try {
          // Update opacity based on which mode is active
          const opacity = isShadowMode
            ? shadowLayerState.opacity / 100
            : sunlightHoursLayerState.opacity / 100

          shadeMapInstance.setOpacity(opacity)

          // Toggle between shadow mode and sun exposure mode
          if (isSunlightHoursMode) {
            // Enable sun exposure mode with sunrise/sunset for the current date

            shadeMapInstance.setSunExposure(true, {
              startDate: sunTimes.sunrise,
              endDate: sunTimes.sunset,
              iterations: 32,
            } as any)

            // Set up tooltip to show hours of sun on hover
            setTimeout(() => {
              setupSunlightHoursTooltip()
            }, 100)
          } else if (isShadowMode) {
            // Disable sun exposure mode (return to shadow mode)
            shadeMapInstance.setSunExposure(false)

            // Remove tooltip handler
            removeSunlightHoursTooltip()
          }
        } catch (error) {
          console.warn('Error updating ShadeMap:', error)
        }
      }
    }

    // Cleanup function - Only cleanup when fundamentals change (style, section, mode)
    // NOT when just the date/time changes - that's handled by the separate effect below
    return () => {
      // Remove mousemove handler if it exists
      if (mouseMoveHandler && map.current) {
        map.current.off('mousemove', mouseMoveHandler)
        mouseMoveHandler = null
      }

      // Remove popup if it exists
      if (popup.current) {
        popup.current.remove()
      }

      // Remove ShadeMap instance
      if (shadeMapInstance) {
        try {
          shadeMapInstance.remove()
          shadeMapInstance = null
        } catch (error) {
          console.warn('[ShadeMap] Error during cleanup:', error)
        }
      }
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.activeSection,
    // Include layer states to detect mode switching - but NOT date changes
    JSON.stringify(
      otherMapOptions.demographicsLayerStates?.['sunlight-shadow-simulation']
    ),
    JSON.stringify(otherMapOptions.demographicsLayerStates?.['sunlight-hours']),
  ])

  // Separate effect for handling date/time updates - this runs without cleanup
  // This allows smooth real-time updates without destroying/recreating the ShadeMap instance
  useEffect(() => {
    if (!map.current || !mapLoaded || !shadeMapInstance) {
      return
    }

    const sunlightConfig = (otherMapOptions as any).sectionData?.sunlightConfig
    const currentDate = sunlightConfig?.date || new Date()

    try {
      // Update date only - this is a lightweight operation that doesn't require full recreation
      shadeMapInstance.setDate(currentDate)
    } catch (error) {
      console.warn('[ShadeMap] Error updating date:', error)
    }

    // NO cleanup - we want the ShadeMap instance to persist
  }, [
    mapLoaded,
    // CRITICAL: Use .getTime() to convert Date to number for proper change detection
    // Date objects are compared by reference, not value, so we need a primitive
    otherMapOptions.sectionData?.sunlightConfig?.date?.getTime(),
  ])
}
